import axios from 'axios'
import msgpack from 'msgpack-lite'
import episodes from '@/services/api/episodes.js'
import * as types from './mutation-types.js'

function getEpisodeStatus (ep) {
  if (ep.is_hidden) {
    return 'private'
  } else if (ep.published_at) {
    return 'live'
  } else if (ep.scheduled_for) {
    return 'scheduled'
  } else {
    return 'draft'
  }
}

async function getEpisodeMeta (ep) {
  ep.season = await episodes.getMeta(ep.season.href)
  let authors = ep.authors.collection.map(a => {
    return episodes.getMeta(a.href)
  })
  ep.authors.collection = await Promise.all(authors)
  ep.status = getEpisodeStatus(ep)
  return ep
}

const actions = {
  async clearEpisode ({ commit }) {
    commit(types.CLEAR_EPISODE)
  },
  clearEpisodes ({ commit }) {
    commit(types.CLEAR_EPISODES)
  },
  createEpisode (context, { podcastId, payload }) {
    return episodes.createEpisode(podcastId, payload).then(episode => episode)
  },
  async getEpisode ({ commit }, episodeId) {
    let ep = await episodes.getEpisode(episodeId)
    commit(types.SET_CURRENT_EPISODE, ep)
    return ep
  },
  async getEpisodes ({ commit }, {podcastId, options}) {
    let result = await episodes.getEpisodes(podcastId, options)

    let episodeList = result.collection
    let episodeCount = result.count

    commit(types.SET_EPISODE_LIST, episodeList)
    commit(types.SET_EPISODE_COUNT, episodeCount)

    if (episodeList.length < 10) {
      return { hasMore: false, hasResults: episodeCount > 0 }
    } else {
      return { hasMore: true, hasResults: episodeCount > 0 }
    }
  },
  async getEpisodeCreationKeys ({ commit }, { podcast }) {
    let keys = await episodes.getCreationKeys(podcast)

    commit(types.SET_EPISODE_CREATION_KEYS, keys)
  },
  async getLastEpisode ({ commit }, { podcastId, options }) {
    let episode = await episodes.getLastEpisode(podcastId, options)

    commit(types.SET_LAST_EPISODE, episode)
  },
  async getTopEpisode ({ commit }, {podcastId, options}) {
    let episode = await episodes.getTopEpisode(podcastId, options)

    commit(types.SET_TOP_EPISODE, episode)
  },
  async loadWaveform (context, { episode, uncache }) {
    // We need a raw call without all of our authentication because S3 doens't like it
    let unauthedAPI = axios.create({
      baseURL: process.env.API_BASE_PATH,
      timeout: 20000
    })
    let waveformUrl = `${episode.waveform_pack}`
    let rid = `${(new Date()).getTime()}${Math.floor(Math.random() * (9999 - 100)) + 100}`

    waveformUrl = uncache ? `${waveformUrl}?${rid}=true` : waveformUrl

    return unauthedAPI({
      method: 'get',
      url: waveformUrl,
      responseType: 'arraybuffer'
    })
      .then((res) => {
        return msgpack.decode(new Uint8Array(res.data))
      })
      .catch((res) => {
        console.error('COULD NOT LOAD WAVEFORM')
      })
  },
  publishEpisode ({ commit }, episodeId) {
    return episodes.publishEpisode(episodeId)
      .then(async ep => {
        const episode = await episodes.getEpisodeMisc(ep)
        commit(types.SET_CURRENT_EPISODE, episode)
      })
  },
  updateEpisodeFeed ({ commit }, { path, payload }) {
    return episodes.updateEpisodeFeed(path, payload)
      .then(async ep => {
        const episode = await episodes.getEpisodeMisc(ep)
        commit(types.SET_CURRENT_EPISODE, episode)
      })
  },
  scheduleEpisode ({ commit }, { episodeId, timestamp }) {
    return episodes.scheduleEpisode(episodeId, timestamp)
      .then(async ep => {
        ep.season = await episodes.getMeta(ep.season.href)
        const episode = await episodes.getEpisodeMisc(ep)
        commit(types.SET_CURRENT_EPISODE, episode)
      })
  },
  unscheduleEpisode ({ commit }, { episodeId }) {
    return episodes.unscheduleEpisode(episodeId)
      .then(async ep => {
        ep.season = await episodes.getMeta(ep.season.href)
        const episode = await episodes.getEpisodeMisc(ep)
        commit(types.SET_CURRENT_EPISODE, episode)
      })
  },
  async updateEpisode ({ commit }, { episodeId, payload }) {
    const ep = await episodes.updateEpisode(episodeId, payload)
    const episode = await episodes.getEpisodeMisc(ep)
    commit(types.SET_CURRENT_EPISODE, episode)
    return episode
  },
  updateEpisodeMeta (context, { path, data }) {
    return episodes.updateEpisodeMeta(path, data).then(async ep => {
      ep = await getEpisodeMeta(ep)
      return ep
    })
  },
  deleteEpisode (context, episodeId) {
    return episodes.deleteEpisode(episodeId)
      .then(async data => {
        return data
      })
      .catch(err => {
        return err
      })
  },
  bulkDeleteEpisodes (context, {podcastId, ids}) {
    return episodes.bulkDeleteEpisodes(podcastId, ids)
      .then(data => {
        return data
      })
      .catch(err => {
        return err
      })
  },
  deleteEpisodeMeta (context, { path, data }) {
    // TODO: Stop making junk like this
    return episodes.deleteFromEpisode(path, data)
      .then(async data => {
        return data
      })
      .catch(err => {
        return err
      })
  }
}

export default actions
